import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { smartFitBounds } from '@/components/time-range-filter'

export interface Viewport {
  start: number
  end: number
}

const MIN_DURATION_MS = 1000

/**
 * Encapsulates the pan/zoom state for the TimelineCanvas.
 *
 * Math mirrors UnifiedTimeline.tsx — cursor-pinned wheel zoom, drag-to-pan,
 * `fitAll` resets to the smart-fit window (P5–P95 of `timestamps` so a few
 * outlier dates don't stretch the axis across decades).
 *
 * `timestamps` is optional; when omitted the reset target is the padded
 * global range, matching the pre-smart behaviour.
 */
export function useTimelineViewport(
  globalStart: number | null,
  globalEnd: number | null,
  timestamps?: number[]
) {
  const pad = globalStart !== null && globalEnd !== null ? (globalEnd - globalStart) * 0.04 : 0
  const paddedStart = globalStart !== null ? globalStart - pad : 0
  const paddedEnd = globalEnd !== null ? globalEnd + pad : 0

  // Smart initial / reset window. Falls back to padded global range when
  // there's no usable timestamp signal (e.g. before data loads).
  const smartTarget = useMemo(() => {
    const fit = timestamps ? smartFitBounds(timestamps) : null
    if (fit) return fit
    return { start: paddedStart, end: paddedEnd }
  }, [timestamps, paddedStart, paddedEnd])

  const [viewport, setViewportState] = useState<Viewport | null>(
    globalStart !== null && globalEnd !== null ? smartTarget : null
  )

  const resetRef = useRef(smartTarget)
  useEffect(() => {
    if (globalStart === null || globalEnd === null) return
    resetRef.current = smartTarget
    setViewportState(smartTarget)
    // We intentionally only re-seed the viewport when the data range itself
    // changes (new case / fresh load). Re-running on every smartTarget
    // identity change would stomp the user's pan/zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalStart, globalEnd])

  // Keep `resetRef` synced with the latest smart target so `fitAll` always
  // uses the current data, even between full re-loads.
  useEffect(() => {
    resetRef.current = smartTarget
  }, [smartTarget])

  const fitAll = useCallback(() => {
    setViewportState(resetRef.current)
  }, [])

  const zoomAtCursor = useCallback(
    (cursorFraction: number, factor: number) => {
      setViewportState(v => {
        if (!v) return v
        const duration = v.end - v.start
        const globalDuration =
          globalStart !== null && globalEnd !== null ? globalEnd - globalStart : duration
        const maxDuration = Math.max(globalDuration * 50, 1000 * 60 * 60 * 24 * 365 * 10)
        let newDuration = duration * factor
        newDuration = Math.max(MIN_DURATION_MS, Math.min(maxDuration, newDuration))
        const anchor = v.start + cursorFraction * duration
        const newStart = anchor - cursorFraction * newDuration
        return { start: newStart, end: newStart + newDuration }
      })
    },
    [globalStart, globalEnd]
  )

  const panBy = useCallback((dxPx: number, widthPx: number) => {
    setViewportState(v => {
      if (!v) return v
      const duration = v.end - v.start
      const dxMs = (dxPx / Math.max(1, widthPx)) * duration
      return { start: v.start - dxMs, end: v.end - dxMs }
    })
  }, [])

  const setViewport = useCallback((next: Viewport) => {
    if (!Number.isFinite(next.start) || !Number.isFinite(next.end) || next.end <= next.start) return
    setViewportState({
      start: next.start,
      end: Math.max(next.start + MIN_DURATION_MS, next.end),
    })
  }, [])

  return { viewport, fitAll, zoomAtCursor, panBy, setViewport }
}
